import { useState, useCallback } from 'react';

/**
 * 管理引導單元分組的自定義 Hook
 * @param {Array} initialGroups - 初始分組陣列
 * @param {Function} onGroupsChange - 分組變更時的回調函數
 * @returns {Object} 分組狀態和操作方法
 */
export const useInitialNodeGroups = (initialGroups = [], onGroupsChange = null) => {
  // 初始化分組，如果為空則創建預設的「活動開始」組
  const [groups, setGroups] = useState(
    initialGroups.length > 0
      ? initialGroups
      : [
          {
            id: 'group-default',
            name: '活動開始',
            timing: 'immediate', // immediate, manual
            description: '這些節點會在活動開始時自動出現',
            nodes: [],
            isDefault: true,
          },
        ]
  );

  const notifyChange = useCallback((newGroups) => {
    setGroups(newGroups);
    if (onGroupsChange) {
      onGroupsChange(newGroups);
    }
  }, [onGroupsChange]);

  // 新增群組
  const addGroup = useCallback(() => {
    const newGroup = {
      id: `group-${Date.now()}`,
      name: `群組 ${groups.length}`,
      timing: 'manual',
      description: '透過小組工具手動添加這些節點',
      nodes: [],
      isDefault: false,
    };
    notifyChange([...groups, newGroup]);
  }, [groups, notifyChange]);

  // 更新群組資訊
  const updateGroup = useCallback((groupId, updates) => {
    const newGroups = groups.map(group =>
      group.id === groupId ? { ...group, ...updates } : group
    );
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 刪除群組
  const removeGroup = useCallback((groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (group?.isDefault) {
      console.warn('無法刪除預設群組');
      return;
    }
    const newGroups = groups.filter(g => g.id !== groupId);
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 在指定群組中新增節點
  const addNodeToGroup = useCallback((groupId, { insertAfter, ...nodeData } = {}) => {
    // Generate a unique negative integer ID (negative to distinguish from real DB node IDs).
    // The ID must fit in a 32-bit INTEGER so that it can be stored in Edge.from / Edge.to columns.
    const allNodeIds = groups.flatMap(g => g.nodes.map(n => n.id)).filter(id => typeof id === 'number');
    const minExistingId = allNodeIds.length > 0 ? Math.min(...allNodeIds) : 0;
    const newNodeId = minExistingId - 1;

    const newGroups = groups.map(group => {
      if (group.id === groupId) {
        const newNode = { id: newNodeId, title: '', content: '', ...nodeData };
        const afterIdx = insertAfter !== undefined ? group.nodes.findIndex(n => n.id === insertAfter) : -1;
        const nodes = [...group.nodes];
        nodes.splice(afterIdx >= 0 ? afterIdx + 1 : nodes.length, 0, newNode);
        return { ...group, nodes };
      }
      return group;
    });
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 更新群組中的節點
  const updateNodeInGroup = useCallback((groupId, nodeId, updates) => {
    const newGroups = groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          nodes: group.nodes.map(node =>
            node.id === nodeId ? { ...node, ...updates } : node
          ),
        };
      }
      return group;
    });
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 從群組中刪除節點
  const removeNodeFromGroup = useCallback((groupId, nodeId) => {
    const newGroups = groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          nodes: group.nodes.filter(node => node.id !== nodeId),
        };
      }
      return group;
    });
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 在群組之間移動節點
  const moveNodeBetweenGroups = useCallback((sourceGroupId, targetGroupId, nodeId) => {
    let nodeToMove = null;
    const newGroups = groups.map(group => {
      if (group.id === sourceGroupId) {
        nodeToMove = group.nodes.find(node => node.id === nodeId);
        return {
          ...group,
          nodes: group.nodes.filter(node => node.id !== nodeId),
        };
      }
      return group;
    });

    if (nodeToMove) {
      const finalGroups = newGroups.map(group => {
        if (group.id === targetGroupId) {
          return {
            ...group,
            nodes: [...group.nodes, nodeToMove],
          };
        }
        return group;
      });
      notifyChange(finalGroups);
    }
  }, [groups, notifyChange]);

  // 重新排序群組
  const reorderGroups = useCallback((startIndex, endIndex) => {
    const newGroups = Array.from(groups);
    const [removed] = newGroups.splice(startIndex, 1);
    newGroups.splice(endIndex, 0, removed);
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 重新排序群組內的節點
  const reorderNodesInGroup = useCallback((groupId, startIndex, endIndex) => {
    const newGroups = groups.map(group => {
      if (group.id === groupId) {
        const newNodes = Array.from(group.nodes);
        const [removed] = newNodes.splice(startIndex, 1);
        newNodes.splice(endIndex, 0, removed);
        return { ...group, nodes: newNodes };
      }
      return group;
    });
    notifyChange(newGroups);
  }, [groups, notifyChange]);

  // 重置所有群組
  const resetGroups = useCallback((newGroups) => {
    notifyChange(newGroups);
  }, [notifyChange]);

  return {
    groups,
    addGroup,
    updateGroup,
    removeGroup,
    addNodeToGroup,
    updateNodeInGroup,
    removeNodeFromGroup,
    moveNodeBetweenGroups,
    reorderGroups,
    reorderNodesInGroup,
    resetGroups,
  };
};

export default useInitialNodeGroups;
